import React, { useState, useEffect } from "react";
import {
  Stack,
  Typography,
  Tooltip,
  LinearProgress,
  Button,
  CssBaseline,
  Box,
  Container,
  Grid,
  Paper,
  Skeleton,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import avisosService from "../../Avisos/avisosService";
import feedbackService from "../../Feedback/feedbackService";
import perguntaDiariaService from "../../PerguntaDiaria/perguntaDiariaService";
import { useNavigate } from "react-router-dom";

const { getAvisos } = avisosService();

export default function DashboardUserView({ uuid }) {
  const [isLoading, setIsLoading] = useState(true);
  const [avisos, setAvisos] = useState([]);
  const [feedbackStatus, setFeedbackStatus] = useState(false);
  const [score, setScore] = useState();
  const { status, scoreSemanal } = feedbackService();
  const { postRespostaDiaria } = perguntaDiariaService();
  const [atividadeFisica, setAtividadeFisica] = useState(null);
  const [exercicioFisico, setExercicioFisico] = useState(null);
  const [jaRespondeuHoje, setJaRespondeuHoje] = useState(false);
  const [respostaDiaria, setRespostaDiaria] = useState({
    atividadeFisica: null,
    exercicioFisico: null,
  });
  const navigate = useNavigate();

  const getColor = (progress) => {
    if (progress <= 24) {
      return "error";
    } else if (progress <= 48) {
      return "warning";
    } else {
      return "success";
    }
  };

  const getPhrase = (progress) => {
    if (progress <= 24) {
      return "Saúde em risco";
    } else if (progress <= 48) {
      return "Você ainda pode melhorar";
    } else {
      return "Você está bem, continue assim";
    }
  };

  useEffect(() => {
    async function getData() {
      try {
        const response = await getAvisos(uuid);
        setAvisos(response.data);
        const statusFeedback = await status(uuid);
        setFeedbackStatus(statusFeedback.data.status);
        const score = await scoreSemanal(uuid);
        setScore(score.data.score);
        setIsLoading(false);
      } catch (error) {
        if (error.response) {
          console.log(error.response.data.message);
        } else {
          console.error(error.message);
        }
      }
    }
    getData();
  }, [uuid]);

  useEffect(() => {
    // Limpa resposta se for de outro dia
    const respostaStorage = localStorage.getItem(`respostaDiaria_${uuid}`);
    const hoje = new Date().toISOString().slice(0, 10);

    if (respostaStorage) {
      const respostaObj = JSON.parse(respostaStorage);
      if (respostaObj.data !== hoje) {
        localStorage.removeItem(`respostaDiaria_${uuid}`);
        setJaRespondeuHoje(false);
        setRespostaDiaria({
          atividadeFisica: null,
          exercicioFisico: null,
        });
        setAtividadeFisica(null);
        setExercicioFisico(null);
      }
    }
  }, [uuid]);

  useEffect(() => {
    // Envia quando ambos forem respondidos e ainda não respondeu hoje
    if (
      atividadeFisica !== null &&
      exercicioFisico !== null &&
      !jaRespondeuHoje
    ) {
      const hoje = new Date().toISOString().slice(0, 10);
      postRespostaDiaria({
        realizou_atividade_fisica: atividadeFisica,
        realizou_exercicio_fisico: exercicioFisico,
        fk_uuid_aluno: uuid,
      })
        .then(() => {
          localStorage.setItem(
            `respostaDiaria_${uuid}`,
            JSON.stringify({
              data: hoje,
              atividadeFisica,
              exercicioFisico,
            })
          );
          setJaRespondeuHoje(true);
          setRespostaDiaria({ atividadeFisica, exercicioFisico });
        })
        .catch((err) => {
          // Trate o erro se necessário
        });
    }
  }, [
    atividadeFisica,
    exercicioFisico,
    jaRespondeuHoje,
    uuid,
    postRespostaDiaria,
  ]);

  return (
    <Container maxWidth="xl">
      <CssBaseline />
      <Box
        sx={{
          marginTop: 2,
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          mb: 3,
        }}
      >
        <Grid container spacing={3} sx={{ display: "flex" }}>
          <Grid item xs={12} md={6}>
            <Paper
              sx={{
                p: 2,
                display: "flex",
                flexDirection: "column",
                height: "100%",
                borderRadius: "16px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  marginBottom: "10px",
                  alignItems: "center",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    color: "#212B2F",
                    fontWeight: "700",
                    fontSize: "1.125rem",
                    lineHeight: "1.5",
                  }}
                >
                  Seu score semanal
                </Typography>
                <Tooltip
                  title="Resultado com base na resposta do Feedback Semanal"
                  sx={{
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginLeft: "4px",
                    marginBottom: "3px",
                  }}
                >
                  <InfoIcon fontSize="small" color="action" />
                </Tooltip>
              </div>
              {isLoading ? (
                <>
                  <Skeleton variant="text" width={50} />
                  <Skeleton variant="text" />
                  <Skeleton variant="text" width={200} />
                </>
              ) : (
                <div
                  style={{
                    position: "relative",
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                  }}
                >
                  <div style={{ display: "flex" }}>
                    <Typography
                      sx={{
                        color: getColor(score),
                        fontWeight: "700",
                        fontSize: "2rem",
                        lineHeight: "1.5",
                      }}
                    >
                      {score}
                    </Typography>
                    <Typography variant="overline">pts</Typography>
                  </div>

                  <LinearProgress
                    value={(score / 72) * 100}
                    variant="determinate"
                    color={getColor(score)}
                  />
                  <Typography color="text.secondary" variant="p">
                    {getPhrase(score)}
                  </Typography>
                </div>
              )}
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper
              sx={{
                p: 2,
                display: "flex",
                flexDirection: "column",
                borderRadius: "16px",
              }}
            >
              <Stack
                alignItems="flex-start"
                direction="row"
                justifyContent="space-between"
                spacing={3}
              >
                <Stack spacing={1}>
                  <Typography
                    variant="h6"
                    sx={{
                      color: "#212B2F",
                      fontWeight: "700",
                      fontSize: "1.125rem",
                      lineHeight: "1.5",
                    }}
                  >
                    Feedback semanal 🤭
                  </Typography>

                  {isLoading ? (
                    <Skeleton variant="text" width={200} />
                  ) : feedbackStatus ? (
                    <Typography variant="p" color="text.secondary">
                      Voce ja respondeu o feedback dessa semana
                    </Typography>
                  ) : (
                    <>
                      <Typography variant="p" color="text.secondary">
                        Responda o feedback semanal
                      </Typography>
                      <div>
                        <Button
                          onClick={() => {
                            navigate(`/feedback/${uuid}`);
                          }}
                        >
                          Responder
                        </Button>
                      </div>
                    </>
                  )}
                </Stack>
              </Stack>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography
              variant="h6"
              sx={{
                color: "#212B2F",
                fontWeight: "700",
                fontSize: "1.125rem",
                lineHeight: "1.5",
                mb: 1,
              }}
            >
              Como foram as suas atividades físicas hoje?
            </Typography>
            <Box
              sx={{
                p: 2,
                borderRadius: "16px",
                overflowX: "auto",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                }}
              >
                <Paper sx={{ minWidth: "200px", padding: 2 }}>
                  <Stack spacing={1}>
                    <Typography
                      variant="h6"
                      sx={{
                        color: "#212B2F",
                        fontWeight: "600",
                        fontSize: "1rem",
                        lineHeight: "1.5",
                      }}
                    >
                      Atividade Física
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      {jaRespondeuHoje ? (
                        <Button
                          variant="contained"
                          color={
                            atividadeFisica === "Sim" ? "success" : "error"
                          }
                          fullWidth
                          sx={{ height: 40 }}
                          disabled
                        >
                          {respostaDiaria.atividadeFisica}
                        </Button>
                      ) : (
                        <>
                          {atividadeFisica === null ||
                          atividadeFisica === "Sim" ? (
                            <Button
                              variant={
                                atividadeFisica === "Sim"
                                  ? "contained"
                                  : "outlined"
                              }
                              color="success"
                              fullWidth
                              sx={{ height: 40 }}
                              onClick={() => setAtividadeFisica("Sim")}
                            >
                              Sim
                            </Button>
                          ) : null}
                          {atividadeFisica === null ||
                          atividadeFisica === "Não" ? (
                            <Button
                              variant={
                                atividadeFisica === "Não"
                                  ? "contained"
                                  : "outlined"
                              }
                              color="error"
                              fullWidth
                              sx={{ height: 40 }}
                              onClick={() => setAtividadeFisica("Não")}
                            >
                              Não
                            </Button>
                          ) : null}
                        </>
                      )}
                    </Stack>
                  </Stack>
                </Paper>
                <Paper sx={{ minWidth: "200px", padding: 2 }}>
                  <Stack spacing={1}>
                    <Typography
                      variant="h6"
                      sx={{
                        color: "#212B2F",
                        fontWeight: "600",
                        fontSize: "1rem",
                        lineHeight: "1.5",
                      }}
                    >
                      Exercício Físico
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      {jaRespondeuHoje ? (
                        <Button
                          variant="contained"
                          color={
                            exercicioFisico === "Sim" ? "success" : "error"
                          }
                          fullWidth
                          sx={{ height: 40 }}
                          disabled
                        >
                          {respostaDiaria.exercicioFisico}
                        </Button>
                      ) : (
                        <>
                          {exercicioFisico === null ||
                          exercicioFisico === "Sim" ? (
                            <Button
                              variant={
                                exercicioFisico === "Sim"
                                  ? "contained"
                                  : "outlined"
                              }
                              color="success"
                              fullWidth
                              sx={{ height: 40 }}
                              onClick={() => setExercicioFisico("Sim")}
                            >
                              Sim
                            </Button>
                          ) : null}
                          {exercicioFisico === null ||
                          exercicioFisico === "Não" ? (
                            <Button
                              variant={
                                exercicioFisico === "Não"
                                  ? "contained"
                                  : "outlined"
                              }
                              color="error"
                              fullWidth
                              sx={{ height: 40 }}
                              onClick={() => setExercicioFisico("Não")}
                            >
                              Não
                            </Button>
                          ) : null}
                        </>
                      )}
                    </Stack>
                  </Stack>
                </Paper>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper
              sx={{
                p: 2,
                display: "flex",
                flexDirection: "column",
                borderRadius: "16px",
              }}
            >
              <Stack
                alignItems="flex-start"
                direction="row"
                justifyContent="space-between"
                spacing={3}
              >
                <Stack spacing={1}>
                  <Typography
                    variant="h6"
                    sx={{
                      color: "#212B2F",
                      fontWeight: "700",
                      fontSize: "1.125rem",
                      lineHeight: "1.5",
                    }}
                  >
                    Mensagens 💬
                  </Typography>

                  {isLoading ? (
                    <Skeleton variant="text" width={200} />
                  ) : avisos !== null ? (
                    <>
                      <Typography color="text.secondary" variant="p">
                        Voce tem mensagens não lidas
                      </Typography>
                      <div>
                        <Button
                          onClick={() => {
                            navigate(`/avisos/${uuid}`);
                          }}
                        >
                          Ler mensagens
                        </Button>
                      </div>
                    </>
                  ) : (
                    <Typography color="text.secondary" variant="p">
                      Voce não tem mensagens
                    </Typography>
                  )}
                </Stack>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
}
